import type { App } from 'vue';
import dayjs from 'dayjs';
import ElementPlus from 'element-plus';
import it from 'element-plus/es/locale/lang/it';
import 'dayjs/locale/it';
import 'element-plus/dist/index.css';

/**
 * La locale di day.js va impostata **oltre** a quella di element-plus, non al suo posto.
 *
 * Le due coprono cose diverse e la seconda non implica la prima: `locale: it` qui sotto fornisce le
 * sole **stringhe** (nomi di mesi e giorni, etichette dei pulsanti), mentre il calendario prende da
 * day.js la struttura della settimana - `use-basic-date-table.mjs` fa
 * `props.date.$locale().weekStart || 7`. Senza questa riga day.js resta in inglese, `weekStart` e 0,
 * e quel `|| 7` fa partire la settimana da **domenica**: i nomi dei giorni si leggono in italiano ma
 * incolonnati sbagliati, che e il modo peggiore di sbagliare perche sembra corretto.
 *
 * `dayjs/locale/it` dichiara `weekStart: 1`. Element UI faceva da se, senza day.js fra le dipendenze.
 *
 * Impostarla globalmente e senza effetti collaterali qui: nessun file del progetto importa day.js
 * direttamente, quindi l'unico consumatore e element-plus.
 *
 * L'impostazione funziona **perche l'istanza e la stessa**: element-plus importa day.js a sua volta, e
 * Vite pre-compila la dipendenza una volta sola. Con due copie in `node_modules` questa riga
 * cambierebbe la locale di una copia che nessuno legge, e il difetto tornerebbe senza sintomi nuovi.
 *
 * ASSUMPTION / debito dichiarato: `dayjs` **non e in `package.json`**, e una transitiva di
 * element-plus (1.11.22 al momento). Oggi si risolve, ma un aggiornamento di element-plus puo
 * cambiarne la versione o farla sparire. Renderla una dipendenza esplicita e la cosa giusta e non
 * scarica nulla di nuovo - il pacchetto e gia nel bundle - ma aggiungere una dipendenza e una
 * decisione da prendere, non da infilare in una correzione.
 */
dayjs.locale('it');

/**
 * Registra element-plus, al posto della registrazione componente per componente di Element UI.
 *
 * **Perche l'installazione completa e non l'elenco.** Prima erano trenta `vue.use(Componente)`
 * scelti a mano: un elenco che va tenuto allineato ai template e che, quando qualcuno usa un
 * componente nuovo senza aggiungerlo qui, produce un `Failed to resolve component` a runtime.
 * Con `app.use(ElementPlus)` il problema sparisce. Il prezzo e un bundle piu grande; per un
 * gestionale interno e un baratto che conviene, e resta reversibile passando all'import su richiesta
 * (`unplugin-vue-components`) quando il porting sara chiuso.
 *
 * **Le proprieta globali restano quelle di prima.** Verificato nel pacchetto installato, non dato
 * per scontato: `ElMessage` si registra come `$message` (`components/message/index.mjs`),
 * `ElNotification` come `$notify`, e `ElMessageBox` installa `$msgbox`, `$messageBox`, `$alert`,
 * `$confirm`, `$prompt` (`components/message-box/index.mjs`). Sono gli stessi nomi che questo file
 * assegnava a mano, quindi i punti che chiamano `this.$message` non vanno toccati.
 *
 * **`vue-data-tables` non c'e piu.** Non ha una versione Vue 3 e dipende da Vue 2 in modo diretto:
 * lasciarlo installato tirerebbe una seconda copia di Vue in `node_modules`. Le pagine con le liste
 * restano rotte finche non viene portato il cluster `liste-datatables`, ed e una rottura dichiarata,
 * non una svista.
 *
 * `ScrollBar`, importato prima da `element-ui/lib/scrollbar.js`, e incluso nell'installazione
 * completa come `ElScrollbar`.
 */
const initElementUI = (app: App) => {
  // La locale `it` di element-plus lascia `pagination.pagesize` in inglese (`"/page"`): verificato
  // in `element-plus/es/locale/lang/it.mjs`, dove `goto` e `total` sono invece tradotti. Element UI
  // mostrava "20/pagina", quindi la stringa si sovrascrive qui invece di accettare la regressione.
  const localeIt = {
    ...it,
    el: { ...it.el, pagination: { ...it.el.pagination, pagesize: '/pagina' } },
  };
  // Element UI non aveva una `size` di default: i componenti erano alti **40px**. element-plus parte da
  // 32px (`--el-component-size`) e chiama `large` i 40 - verificato in `element-plus/dist/index.css`.
  // Senza questa riga tutta l'applicazione risulta rimpicciolita di 8px, che e il rilievo della PO sui
  // bottoni "in generale piu piccoli" e sui campi "di dimensione diversa".
  app.use(ElementPlus, { locale: localeIt, size: 'large' });
};

export default initElementUI;
